import '@mantine/core/styles.css';
import 'react-toastify/dist/ReactToastify.css';
import './assets/stylesheets/landings.css';
import './styles/override/mantine-override.scss';

import 'reflect-metadata';
import {MantineProvider} from '@mantine/core';
import {ErrorBoundary} from '@sentry/react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {AxiosError} from 'axios';
import {Provider} from 'react-redux';
import {Router} from 'react-router';
import {ToastContainer} from 'react-toastify';

import Routes from 'MainRoutes';
import {initI18n} from 'react-i18next.config';
import {routerHistory} from 'routerHistory';
import EventManager from 'services/EventManager';
import {TasksObserverProvider} from 'services/TasksObserver/TasksObserverProvider';
import AuthUserProvider from 'shared/components/AuthUserProvider';
import {ConformationProvider} from 'shared/components/Confirmation';
import {initAgGrid} from 'shared/helpers/ag-grid';
import {initDayJs} from 'shared/helpers/dates';
import {initSentry} from 'shared/helpers/sentry';
import {queryCacheHelper} from 'shared/hooks/useQueryCache/QueryCacheHelper';
import store from 'store';
import {theme} from 'styles/theme';

initAgGrid();
initSentry();
initDayJs();
initI18n();

export const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: (_failureCount, error: unknown) => {
        if (error instanceof AxiosError) {
          return ![401, 403, 404].includes(error.response?.status ?? 0);
        }
        return true;
      },
    },
  },
});

EventManager.initPixel();
EventManager.initMixpanel();
queryCacheHelper.initialize(reactQueryClient);

const App = () => {
  return (
    <QueryClientProvider client={reactQueryClient}>
      <TasksObserverProvider>
        <ReactQueryDevtools initialIsOpen={false} />
        <Router history={routerHistory}>
          <Provider store={store}>
            <AuthUserProvider>
              <ErrorBoundary showDialog>
                <MantineProvider theme={theme}>
                  <ConformationProvider>
                    <Routes />
                  </ConformationProvider>
                </MantineProvider>
              </ErrorBoundary>
            </AuthUserProvider>
          </Provider>
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <ToastContainer
              autoClose={4000}
              className="Toastify-core"
              hideProgressBar={false}
              pauseOnFocusLoss={false}
              position="bottom-right"
              style={{zIndex: 99999}}
            />
          </div>
        </Router>
      </TasksObserverProvider>
    </QueryClientProvider>
  );
};

export default App;
